import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import { ArrowLeft, MapPin, Camera, MessageSquare, CheckCircle2, Clock, Send, Loader2, PlayCircle } from "lucide-react";

const PRIORITY = {
  urgent: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  high: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  medium: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  low: "border-zinc-500/40 text-zinc-300 bg-zinc-500/10",
};
const STATUS_TONE = {
  open: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  in_progress: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  completed: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  closed: "border-zinc-500/40 text-zinc-300 bg-zinc-500/10",
};

/**
 * Mobile-first vendor ticket detail — field-friendly.
 * Vendors update status, add notes, upload photos, and mark the job
 * complete without leaving one screen. Designed for 375×812 first;
 * two-column at md+.
 */
export default function VendorTicketDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();

  const [ticket, setTicket] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/tickets");
      const t = (data.items || []).find((x) => x.id === id);
      setTicket(t || null);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    // Timeline is only accessible once assigned — swallow 403 silently.
    try {
      const { data: tl } = await api.get(`/maintenance/${id}/timeline`);
      setTimeline(tl.events || []);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/vendor/VendorTicketDetailPage.jsx: swallowed', err); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/maintenance/${id}`, { status });
      toast.success(`Status → ${status.replace("_", " ")}`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const sendNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/maintenance/${id}/timeline`, { kind: "vendor_note", body: note.trim() });
      setNote("");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("Max 15 MB");
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/maintenance/${id}/timeline/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
      e.target.value = "";
      await load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
    setUploading(false);
  };

  if (!ticket) return (
    <div className="p-4 sm:p-6">
      <Button variant="outline" size="sm" onClick={() => nav("/vendor/tickets")} className="border-white/10 mb-4" data-testid="vd-back">
        <ArrowLeft className="h-3 w-3 mr-1" /> Back
      </Button>
      <div className="text-sm text-zinc-500" data-testid="vd-empty">Loading ticket…</div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4 pb-24" data-testid="vd-page">
      <Button variant="outline" size="sm" onClick={() => nav("/vendor/tickets")} className="border-white/10" data-testid="vd-back">
        <ArrowLeft className="h-3 w-3 mr-1" /> Back to tickets
      </Button>

      {/* Header */}
      <Card className="border-white/10 bg-card p-4 sm:p-5">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Badge variant="outline" className={PRIORITY[ticket.priority] || "border-white/10"}>{ticket.priority}</Badge>
          <Badge variant="outline" className={STATUS_TONE[ticket.status] || "border-white/10"} data-testid="vd-status">{(ticket.status || "").replace("_", " ")}</Badge>
          <div className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> Unit {ticket.unit}</div>
        </div>
        <div className="text-xl sm:text-2xl font-semibold font-display leading-tight" data-testid="vd-title">{ticket.title}</div>
        <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap" data-testid="vd-desc">{ticket.description}</div>

        {ticket.photos?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
              <Camera className="h-3 w-3" /> Site photos ({ticket.photos.length})
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ticket.photos.map((p) => (
                <AuthImage key={p.path} path={p.path} className="aspect-square rounded-md object-cover border border-white/10" />
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Status actions — mobile-first stacked */}
      <Card className="border-white/10 bg-card p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Update status</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Button onClick={() => setStatus("in_progress")} disabled={busy || ticket.status === "in_progress"} className="w-full bg-indigo-500 hover:bg-indigo-600" data-testid="vd-start">
            <PlayCircle className="h-4 w-4 mr-1" /> Start work
          </Button>
          <Button onClick={() => setStatus("completed")} disabled={busy || ticket.status === "completed"} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="vd-complete">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Mark complete
          </Button>
          <Button onClick={() => setStatus("open")} disabled={busy || ticket.status === "open"} variant="outline" className="w-full border-white/10 text-zinc-300" data-testid="vd-reopen">
            <Clock className="h-4 w-4 mr-1" /> Reopen
          </Button>
        </div>
      </Card>

      {/* Timeline */}
      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-white/10 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <MessageSquare className="h-3 w-3" /> Timeline ({timeline.length})
          </div>
          <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200" data-testid="vd-photo-label">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
            <span>{uploading ? "Uploading…" : "Add photo"}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadPhoto} className="hidden" data-testid="vd-photo-input" />
          </label>
        </div>
        <div className="max-h-[50vh] overflow-y-auto divide-y divide-white/5 scroll-slim" data-testid="vd-timeline-list">
          {timeline.length === 0 && (
            <div className="p-6 text-center text-sm text-zinc-500" data-testid="vd-timeline-empty">No events yet. Add the first note below.</div>
          )}
          {timeline.map((e) => (
            <div key={e.id} className="p-3 sm:p-4 text-sm" data-testid={`vd-event-${e.id}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                <span>{e.kind?.replace("_", " ")}</span>
                <span>·</span>
                <span>{e.actor_name || e.actor_email || "system"}</span>
                <span>·</span>
                <span>{new Date(e.at).toLocaleString()}</span>
              </div>
              {e.body && <div className="text-zinc-300 whitespace-pre-wrap">{e.body}</div>}
              {e.photo_id && (
                <AuthImage path={`/maintenance/timeline/photos/${e.photo_id}`} className="mt-2 max-h-48 rounded-md border border-white/10" data-testid={`vd-event-photo-${e.id}`} />
              )}
            </div>
          ))}
        </div>
        <div className="p-3 sm:p-4 border-t border-white/10 space-y-2">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note from the field…" className="bg-white/[0.02] border-white/10" data-testid="vd-note-input" />
          <Button onClick={sendNote} disabled={busy || !note.trim()} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="vd-note-send">
            <Send className="h-3 w-3 mr-1" /> Post note
          </Button>
        </div>
      </Card>
    </div>
  );
}
